/**
 * App-owned web weekly MFA (email OTP) — not Cognito User Pool MFA.
 *
 * Audience: everyone except members & independents (includes L1–L4 staff).
 * Clock: 7×24h from last successful verify, per browser/deviceId.
 */

import {
  isIndependentUser,
  isMember,
  type RoleProfile,
} from '../rbac/member-roles'

/** Soft-lock window after a successful web MFA verify. */
export const WEB_MFA_TTL_MS = 7 * 24 * 60 * 60_000

/**
 * A stored "last verified/unlocked" timestamp further than this in the future
 * is treated as expired. Guards against device-clock rollback (or a tampered
 * localStorage value) parking the clock in the future and silencing the
 * re-verify policy indefinitely.
 */
export const CLOCK_SKEW_TOLERANCE_MS = 5 * 60_000

/** OTP lifetime once issued. */
export const WEB_MFA_OTP_TTL_MS = 10 * 60_000

/** Minimum gap between OTP emails (start or resend). */
export const WEB_MFA_RESEND_COOLDOWN_MS = 60_000

/** Max verify attempts per issued code before the challenge is invalidated. */
export const WEB_MFA_MAX_ATTEMPTS = 5

export const WEB_MFA_OTP_LENGTH = 6

export const WEB_MFA_VERIFY_COPY = "Verify it's you"

export type WebMfaChallengeKind = 'app-owned-email-otp'

export type WebMfaPolicy = {
  enforced: true
  ttlMs: typeof WEB_MFA_TTL_MS
  challenge: WebMfaChallengeKind
}

/** Live policy — web gate must enforce this (not Cognito MFA). */
export const WEB_MFA_POLICY: WebMfaPolicy = {
  enforced: true,
  ttlMs: WEB_MFA_TTL_MS,
  challenge: 'app-owned-email-otp',
}

/**
 * Weekly MFA audience: all signed-in roles except members & independents.
 * L4 Provider Owner is included (same as other staff).
 */
export function requiresWebWeeklyMfa(profile: RoleProfile): boolean {
  return !isMember(profile) && !isIndependentUser(profile)
}

export function isWebMfaClockExpired(
  lastVerifiedAt: number | null,
  ttlMs: number = WEB_MFA_TTL_MS,
  now: number = Date.now(),
): boolean {
  if (lastVerifiedAt == null) return true
  if (!Number.isFinite(lastVerifiedAt)) return true
  if (lastVerifiedAt - now > CLOCK_SKEW_TOLERANCE_MS) return true
  return now - lastVerifiedAt >= ttlMs
}

/** Mask email for UI / API hints: j***@example.com */
export function maskEmail(email: string): string {
  const trimmed = email.trim()
  const at = trimmed.indexOf('@')
  if (at <= 0) return '***'
  const local = trimmed.slice(0, at)
  const domain = trimmed.slice(at + 1)
  const visible = local.slice(0, 1)
  return `${visible}***@${domain}`
}

export function normalizeOtpInput(raw: string): string {
  return String(raw ?? '')
    .replace(/\s+/g, '')
    .replace(/\D/g, '')
    .slice(0, WEB_MFA_OTP_LENGTH)
}
